import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Text, View, Pressable, StyleSheet, Dimensions, Image } from 'react-native';
import {
  COLOR,
  FONT,
  FONT_SIZE,
  THEME,
  __WEB__,
  SELECT_STYLE_ROUND,
} from '../../../constants/styles';
import CommonStyles from '../../auth/common_styles';
import { Select, Utils } from '../../components';
import {
  EVENT_TYPE_PHA,
  EVENT_TYPE_PHA_FLY,
  EVENT_TYPE_OHE,
  EVENT_TYPE_AUDIOGRAM,
  EVENT_TYPE_SPIROMETRY,
  EVENT_TYPE_LAB,
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
  EVENT_TYPE_DENTAL,
  EVENT_TYPE_SECURITY_FORCES_PCM,
  EVENT_TYPE_SHPE,
  EVENT_TYPE_AUDIOGRAM_SPIROMETRY,
  EVENT_TYPE_SERE_ECAC,
  EVENT_TYPE_IMAGING_IMPORT_PICKUP,
  EVENT_TYPE_ANAM_TEST,
  EVENT_TYPE_IMMUNIZATION,
  EVENT_TYPE_DRHA,
  EVENT_TYPE_COMMUNITY_HEALTH_INTERVIEW,
  EVENT_TYPE_TRAVEL_MEDICINE,
  EVENT_TYPE_SPECIALTY_HEARING,
} from '../../../constants/eventTypes';

import {
  DEMO,
  JBSA_PROVIDERS,
  MACDILL,
  TRAVIS,
  TRAVIS_AMDS_349,
} from '../../../constants/base_keys';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { fetchBaseInfo } from './slice';
import { appointmentLabelForType } from '../scheduler/common_views';

const REQUEST_FORM = 1;
const CANCEL_FORM = 2;

import RequestForm from './request_form';
import AppointmentForm from './appointment_requests';
import CancelForm from './cancel_form';
import CMRReqStep1 from '../scheduler/cmr_req_step1';

const SELECT_STYLE = {
  ...SELECT_STYLE_ROUND,
  menuList: (base, state) => ({
    // Overriding the menu list style in order to explicitly NOT set a height.
    ...base,
    zIndex: 9999,
  }),
  singleValue: (styles) => {
    return { ...styles, color: 'white' };
  },
  placeholder: (styles) => {
    return { ...styles, color: 'white' };
  },
  control: (styles) => ({
    ...styles,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'white',
    backgroundColor: 'transparent',
    paddingLeft: 20,
  }),
};

const SLUGS = {
  ohe: EVENT_TYPE_OHE,
  flypha: EVENT_TYPE_PHA_FLY,
  pha: EVENT_TYPE_PHA,
  audiogram: EVENT_TYPE_AUDIOGRAM,
  spirometry: EVENT_TYPE_SPIROMETRY,
  lab: EVENT_TYPE_LAB,
  'security-forces-pcm': EVENT_TYPE_SECURITY_FORCES_PCM,
  cmr: EVENT_TYPE_CMR,
  dental: EVENT_TYPE_DENTAL,
  shpe: EVENT_TYPE_SHPE,
  'sere-ecac': EVENT_TYPE_SERE_ECAC,
  'audiogram-spirometry': EVENT_TYPE_AUDIOGRAM_SPIROMETRY,
  'imaging-import-pickup': EVENT_TYPE_IMAGING_IMPORT_PICKUP,
  'anam-test': EVENT_TYPE_ANAM_TEST,
  immunization: EVENT_TYPE_IMMUNIZATION,
  drha: EVENT_TYPE_DRHA,
  'travel-medicine': EVENT_TYPE_TRAVEL_MEDICINE,
  'specialty-hearing': EVENT_TYPE_SPECIALTY_HEARING,
  'community-health-interview': EVENT_TYPE_COMMUNITY_HEALTH_INTERVIEW,
};

const BaseUnauthed = memo((props) => {
  const locationPath = window.location.pathname.toLowerCase();
  const baseParts = locationPath.split('base/')[1];
  const functionOrEventType = (baseParts?.split('/')?.[1] || '').toLowerCase();
  let slugEventType = null;
  let cancelToken = null;
  let requestAccess = false;
  if (functionOrEventType === 'cancel') {
    cancelToken = new URLSearchParams(location.search).get('token');
  } else if (functionOrEventType === 'request-access') {
    requestAccess = true;
  } else if (functionOrEventType) {
    slugEventType = SLUGS[functionOrEventType];
  }

  const initFormType = !!cancelToken
    ? CANCEL_FORM
    : props.base === JBSA_PROVIDERS || requestAccess
      ? REQUEST_FORM
      : null;

  const { width } = Dimensions.get('window');
  const [isVerticalLayout, setIsVerticalLayout] = useState(width < 1200);
  const [widthDim, setWidthDim] = useState(width);
  const [formType, setFormType] = useState(initFormType);
  const [eventTypes, setEventTypes] = useState([]);
  const [preReqsValid, setPreReqsValid] = useState(false);

  useEffect(() => {
    if (props.eventTypesConfiguration && !formType) {
      const eventTypes = props.eventTypesConfiguration.eventTypesPublic || [];
      const newFormType =
        slugEventType && eventTypes.includes(slugEventType) ? slugEventType : eventTypes[0];

      setEventTypes(eventTypes);
      setFormType(newFormType);
    }
  }, [props.eventTypesConfiguration, props.base, formType]);

  const handleDimsChange = useCallback(({ window: { width, height } }) => {
    setIsVerticalLayout(width < 1200);
    setWidthDim(width);
  }, []);

  useEffect(() => {
    props.actions.fetchBaseInfo({ employerKey: props.base });
    const subscription = Dimensions.addEventListener('change', handleDimsChange);
    return () => {
      subscription.remove();
    };
  }, [handleDimsChange]);

  function errorMessageFromProps() {
    if (props.baseInfoStatus === 'failed') {
      return `invalid base: ${props.base}`;
    }
    if (props.error?.message?.[0] === 'invalid-input-response')
      return 'invalid captcha input, please try again';
    return props.error?.message;
  }

  const handleChangeApptType = useCallback(({ id }) => {
    setFormType(id);
    setPreReqsValid(false);
  }, []);

  const selectCMRType = useCallback((type) => {
    setFormType(type);
    setPreReqsValid(true);
  }, []);
  const unselectCMRType = useCallback(() => {
    setFormType(EVENT_TYPE_CMR);
    setPreReqsValid(false);
  }, []);

  function renderCancelAppt() {
    if (formType !== CANCEL_FORM) return null;
    return (
      <CancelForm
        token={cancelToken}
        captchaDisabled={true}
        phone={props.base === TRAVIS ? '707-423-3999' : undefined}
      />
    );
  }

  function renderAccessRequest() {
    if (formType !== REQUEST_FORM) return null;
    return (
      <RequestForm
        errorMessage={errorMessageFromProps()}
        base={props.base}
        isVerticalLayout={isVerticalLayout}
        captchaDisabled={true}
      />
    );
  }

  function renderApptRequest() {
    if (formType === REQUEST_FORM || formType === CANCEL_FORM) return null;

    // widthDim is passed as a prop to trigger a re-render when the layout has changed
    if (!formType) {
      return (
        <View style={{ flex: 1, alignItems: 'center', padding: 20, paddingTop: 50 }}>
          <Text style={[THEME.textBold, { fontSize: 24 }]}>No schedules available</Text>
        </View>
      );
    }

    // if (formType === EVENT_TYPE_CMR && props.base === TRAVIS) {
    if (!preReqsValid && (formType === EVENT_TYPE_CMR || formType === EVENT_TYPE_CMR_FLY)) {
      return <CMRReqStep1 isVerticalLayout={width < 600} onCMRTypeSelected={selectCMRType} />;
    }

    return (
      <AppointmentForm
        errorMessage={errorMessageFromProps()}
        base={props.base}
        timeZone={props.timeZone}
        isVerticalLayout={isVerticalLayout}
        eventType={formType}
        widthDim={widthDim}
        back={unselectCMRType}
        eventTypesConfig={props.eventTypesConfiguration || {}}
        userData={props.userData}
      />
    );
  }

  const openPhaVideo = useCallback(() => {
    window.open(
      'https://getoutpatient.com/wp-content/uploads/2026/06/Outpatient-App-Mobile-Training-Video.mp4',
      '_blank'
    );
  }, []);

  const showHelp = useCallback(() => {
    return formType !== CANCEL_FORM && formType !== REQUEST_FORM;
  }, [formType]);

  const apptTypeOptions = useMemo(() => {
    const eventTypeToFilter = formType === EVENT_TYPE_CMR_FLY ? EVENT_TYPE_CMR : EVENT_TYPE_CMR_FLY;
    const adjustedEventTypes = eventTypeToFilter
      ? eventTypes.filter((et) => et !== eventTypeToFilter)
      : eventTypes;
    return adjustedEventTypes.map((eventType) => {
      const eventTypeLabel = appointmentLabelForType(eventType);
      return {
        label: eventTypeLabel,
        id: eventType,
      };
    });
  }, [eventTypes, formType]);

  const isMacDill = props.base === MACDILL;
  const showNonFlyPHABannerMacDill = formType === EVENT_TYPE_PHA && isMacDill;
  const showAudiogramBannerMacDill = formType === EVENT_TYPE_AUDIOGRAM && isMacDill;
  const showSpecialtyHearingBannerMacDill = formType === EVENT_TYPE_SPECIALTY_HEARING && isMacDill;
  const employerLogo = props.employerId ? Utils.employerImagePath(props.employerId) : null;

  return (
    <View style={isVerticalLayout ? styles.containerVertical : styles.containerHorizontal}>
      <View style={isVerticalLayout ? styles.verticalNavContainer : styles.horizontalNavContainer}>
        <View
          style={isVerticalLayout ? styles.innerNavContainerVertical : styles.innerNavContainer}
        >
          <View style={isVerticalLayout ? { alignItems: 'flex-start' } : null}>
            <View style={[styles.branding, isVerticalLayout ? { marginBottom: 0 } : null]}>
              {!!employerLogo && (
                <Image style={styles.brandingImage} source={{ uri: employerLogo }} />
              )}
              {formType === CANCEL_FORM || formType === REQUEST_FORM ? (
                <View style={styles.brandingTextContainer}>
                  <Text style={styles.brandingText}>{props.baseName}</Text>
                </View>
              ) : (
                <View style={styles.brandingTextContainer}>
                  <Text style={styles.brandingText}>{props.baseName}</Text>
                  <Text style={styles.requestApptTimeText}>Request Appointment</Text>
                </View>
              )}
            </View>
            <View
              style={[
                { zIndex: 1000 },
                !!isVerticalLayout ? styles.optionsContainerVertical : styles.optionsContainer,
              ]}
            >
              {!isVerticalLayout && formType !== CANCEL_FORM && formType !== REQUEST_FORM && (
                <View style={styles.btn}>
                  <Text style={styles.btnText}>Appointment Type:</Text>
                </View>
              )}
              {isVerticalLayout && formType !== CANCEL_FORM && formType !== REQUEST_FORM && (
                <View style={styles.btn}>
                  <Text style={styles.btnText}>Appointment Type:</Text>
                </View>
              )}
              {!!apptTypeOptions?.length &&
                formType !== CANCEL_FORM &&
                formType !== REQUEST_FORM && (
                  <View
                    style={[{ zIndex: 1000 }, isVerticalLayout ? { width: 300 } : { width: 350 }]}
                  >
                    <Select
                      closeOnSelect={true}
                      options={apptTypeOptions}
                      value={apptTypeOptions.find((o) => o.id === formType) || null}
                      onValue={handleChangeApptType}
                      webMode={true}
                      styles={SELECT_STYLE}
                      menuPortalTarget={__WEB__ ? document.body : null}
                    />
                    {!!showNonFlyPHABannerMacDill && (
                      <View style={styles.specialBanner}>
                        <Text style={styles.specialBannerText}>
                          Non-Air Force members please ONLY book into these times:
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 15 }]}>
                          <Text style={styles.bullet}>{'\u2022'}</Text> 0805
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 5 }]}>
                          <Text style={styles.bullet}>{'\u2022'}</Text> 0835
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 5 }]}>
                          <Text style={styles.bullet}>{'\u2022'}</Text> 1105
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 5 }]}>
                          <Text style={styles.bullet}>{'\u2022'}</Text> 1325
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 5 }]}>
                          <Text style={styles.bullet}>{'\u2022'}</Text> 1405
                        </Text>
                        <Text style={[styles.specialBannerText, { marginTop: 15 }]}>
                          USSOCOM members please contact SOCOM Clinic to schedule appointments.
                        </Text>
                      </View>
                    )}
                    {!!showAudiogramBannerMacDill && (
                      <View style={styles.specialBanner}>
                        <Text style={styles.specialBannerText}>
                          By submitting this request, you understand that this is NOT a confirmed
                          appointment. Requests must be reviewed and approved by Public Health
                          before an appointment is scheduled. Confirmation or rejection will be sent
                          via text and email.
                        </Text>
                      </View>
                    )}
                    {!!showSpecialtyHearingBannerMacDill && (
                      <View style={styles.specialBanner}>
                        <Text style={styles.specialBannerText}>
                          If you've already completed your Public Health Audiogram and have been
                          instructed to set up a Specialty Hearing appointment, this is the correct
                          selection.
                        </Text>
                      </View>
                    )}
                  </View>
                )}
            </View>
          </View>
          {!isVerticalLayout && formType !== CANCEL_FORM && formType !== REQUEST_FORM && (
            <>
              <View style={styles.helpContainerHorizontal}>
                <Pressable style={styles.phaVideoContainer} onPress={openPhaVideo}>
                  <Text style={styles.needHelpText}>Need Help?</Text>
                  <View style={THEME.basicRow}>
                    <Image
                      style={styles.videoImage}
                      source={require('../../../../resources/user-guide-video-preview.png')}
                    />
                    <Text style={styles.watchDemoText}>Watch Demo Video</Text>
                  </View>
                </Pressable>
              </View>
              <View style={styles.poweredContainer}>
                <Image
                  style={styles.poweredLogo}
                  source={require('../../../../resources/icon.png')}
                />
                <Text style={styles.poweredText}>POWERED BY OUTPATIENT APP</Text>
              </View>
            </>
          )}
        </View>
      </View>

      {renderCancelAppt()}
      {renderAccessRequest()}
      {renderApptRequest()}

      {!!isVerticalLayout && formType !== CANCEL_FORM && formType !== REQUEST_FORM && (
        <View style={styles.helpContainer}>
          <>
            <Pressable style={styles.phaVideoContainerVertical} onPress={openPhaVideo}>
              <Text style={styles.needHelpText}>Need Help?</Text>
              <View style={THEME.basicRow}>
                <Image
                  style={styles.videoImage}
                  source={require('../../../../resources/user-guide-video-preview.png')}
                />
                <Text style={styles.watchDemoText}>Watch Demo Video</Text>
              </View>
            </Pressable>
            <View style={styles.poweredContainer}>
              <Image
                style={styles.poweredLogo}
                source={require('../../../../resources/icon.png')}
              />
              <Text style={styles.poweredText}>POWERED BY OUTPATIENT APP</Text>
            </View>
          </>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  ...CommonStyles,
  containerVertical: {
    position: 'relative',
    minHeight: '100%',
  },
  containerHorizontal: {
    flexDirection: 'row',
    minHeight: '100%',
  },
  horizontalNavContainer: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: 'rgb(37,47,62)',
  },
  verticalNavContainer: {
    height: 'auto',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgb(37,47,62)',
    zIndex: 10,
    padding: 10,
  },
  innerNavContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    paddingVertical: 20,
  },
  innerNavContainerVertical: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  optionsContainer: {},
  optionsContainerVertical: {
    flexDirection: 'column',
    marginTop: 10,
  },
  btn: {
    flexDirection: 'row',
    height: 30,
    alignItems: 'center',
  },
  btnText: {
    ...FONT.bold,
    color: '#e5e6e9',
  },
  needHelpText: {
    ...FONT.light,
    color: 'white',
    paddingVertical: 10,
    fontSize: FONT_SIZE.enormous,
  },
  phaVideoContainer: {
    marginTop: 50,
  },
  phaVideoContainerVertical: {},
  videoImage: {
    width: 200,
    height: 126,
  },
  watchDemoText: {
    ...FONT.light,
    color: 'white',
    textDecorationLine: 'underline',
    padding: 10,
  },
  branding: {
    marginBottom: 40,
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandingImage: {
    width: 70,
    height: 70,
  },
  brandingTextContainer: {
    flexDirection: 'column',
    marginLeft: 14,
    paddingVertical: 10,
  },
  brandingText: {
    ...FONT.bold,
    fontSize: FONT_SIZE.huge,
    color: COLOR.darkTheme.text,
    marginLeft: 4,
  },
  requestApptTimeText: {
    ...FONT.semibold,
    fontSize: FONT_SIZE.xlarge,
    color: COLOR.redOrangeColor,
    marginLeft: 4,
  },
  helpContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgb(37,47,62)',
    paddingVertical: 35,
  },
  helpContainerHorizontal: {
    paddingVertical: 10,
  },
  specialBanner: {
    borderRadius: 8,
    borderWidth: 2,
    borderColor: COLOR.emphasis,
    marginBottom: 6,
    paddingVertical: 11,
    paddingHorizontal: 15,
    backgroundColor: 'rgb(24, 33, 52)',
  },
  specialBannerText: {
    ...FONT.semibold,
    color: 'white',
    fontSize: 16,
  },
  poweredLogo: {
    height: 40,
    width: 40,
  },
  poweredContainer: {
    paddingVertical: 20,
    flexDirection: 'row',
    alignItems: 'center',
  },
  poweredText: {
    ...FONT.semibold,
    color: 'white',
    fontSize: 12,
    marginLeft: 10,
    letterSpacing: 3,
    opacity: 0.96,
  },
});

function mapStateToProps(state, ownProps) {
  const formInfo = state.screens.baseUnauthed.baseInfoData;
  return {
    eventTypesConfiguration: formInfo ? formInfo?.eventTypesConfiguration || {} : null,
    employerId: formInfo?.employerId ?? null,
    baseName: formInfo ? formInfo?.name || {} : null,
    timeZone: state.screens.baseUnauthed.baseInfoData?.timeZone,
    error: state.screens.baseUnauthed.error,
    baseInfoStatus: state.screens.baseUnauthed.baseInfoStatus,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ fetchBaseInfo }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BaseUnauthed);
